import { useEffect, useMemo, useState } from 'react';
import { useDialog } from '../lib/useDialog';
import { useNavigate } from 'react-router-dom';
import { Check, HardHat, Mail, Plus, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { uploadFile } from '../lib/upload';
import { useI18n } from '../lib/i18n';
import { AddressAutocomplete } from './AddressAutocomplete';
import { FileDrop } from './FileDrop';
import { CLAIM_TYPES } from '../lib/claimType';
import { stateFromAddress } from '../lib/usState';

type Contractor = { id: string; name: string; email?: string | null; pa_name?: string | null; fee_percent?: number | null; default_team_id?: string | null };
type Team = { id: string; name: string };
type Insurance = { id: string; name: string };

/** Modal "Add Lead": crea un Lead manual (contratista → autollena PA + account manager) + enviar/subir contrato. */
export function AddLeadModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const dialogo = useDialog(onClose);
  const nav = useNavigate();
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [insurers, setInsurers] = useState<Insurance[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [f, setF] = useState({ full_name: '', email: '', phone: '', address: '', contractor_id: '', account_team_id: '', insurance_company: '', policy_number: '', claim_number: '', photos_link: '', claim_type: '', state: '', preferred_language: '', gate_code: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [claimId, setClaimId] = useState<string | null>(null);
  const [fee, setFee] = useState('');
  const [feeSource, setFeeSource] = useState('none');
  // `from`/`team`: DE DÓNDE sale el PA impreso (equipo asignado = la fuente; lo demás, respaldo).
  // `blocked`: el PA del equipo no está licenciado en el estado → el contrato no puede salir.
  const [pa, setPa] = useState<{
    name: string | null; license: string | null; missing: boolean; state: string | null;
    from: string | null; team: string | null; blocked: { pa_name: string; team_name: string } | null;
  }>({ name: null, license: null, missing: false, state: null, from: null, team: null, blocked: null });
  // La plantilla que va a salir (null hasta que responde la sugerencia).
  const [tpl, setTpl] = useState<{ id: string; name: string; special_for: string | null } | null>(null);
  // El estado del form contradice el de la dirección → el backend no va a generar el contrato.
  const [choqueEstado, setChoqueEstado] = useState<{ form: string; address: string } | null>(null);
  const [insOther, setInsOther] = useState(false); // aseguradora nueva: texto libre en vez del dropdown
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));

  // Al crear el lead, sugiere el fee% (contratista o default del estado) + el PA que
  // saldrá en el contrato (por LICENCIA del estado) para verlos antes de enviar.
  useEffect(() => {
    if (!claimId) return;
    api.get<{ fee_percent: number | null; source: string; pa_name: string | null; pa_license: string | null; pa_missing: boolean; state: string | null; pa_from: string | null; pa_team: string | null; pa_blocked: { pa_name: string; team_name: string } | null; template: { id: string; name: string; special_for: string | null } | null; state_mismatch: { form: string; address: string } | null }>(`/api/claims/${claimId}/contract/suggestion`)
      .then((r) => {
        setFee(r.fee_percent != null ? String(r.fee_percent) : ''); setFeeSource(r.source);
        setPa({
          name: r.pa_name ?? null, license: r.pa_license ?? null, missing: Boolean(r.pa_missing), state: r.state ?? null,
          from: r.pa_from ?? null, team: r.pa_team ?? null, blocked: r.pa_blocked ?? null,
        });
        setTpl(r.template ?? null);
        setChoqueEstado(r.state_mismatch ?? null);
      }).catch(() => {});
  }, [claimId]);

  useEffect(() => {
    api.get<{ contractors: Contractor[] }>('/api/contractors/picklist').then((r) => setContractors(r.contractors)).catch(() => {});
    api.get<{ teams: Team[] }>('/api/teams/options?department=claims').then((r) => setTeams(r.teams)).catch(() => {});
    api.get<{ insurance_companies: Insurance[] }>('/api/insurance-companies').then((r) => setInsurers(r.insurance_companies)).catch(() => {});
    api.get<{ states: string[] }>('/api/intake/states').then((r) => setStates(r.states)).catch(() => {});
  }, []);

  const contractor = useMemo(() => contractors.find((x) => x.id === f.contractor_id), [contractors, f.contractor_id]);
  // Al elegir contratista, autollena account manager con su team default (editable).
  function pickContractor(id: string) {
    const c = contractors.find((x) => x.id === id);
    setF((p) => ({ ...p, contractor_id: id, account_team_id: c?.default_team_id ? String(c.default_team_id) : p.account_team_id }));
  }

  async function create() {
    if (!f.full_name.trim() || !f.address.trim()) { setErr(t('al.needNameAddr')); return; }
    setBusy(true); setErr('');
    try { const r = await api.post<{ claimId: string }>('/api/intake/lead', f); setClaimId(r.claimId); onCreated(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function sendContract() {
    if (!claimId) return;
    setBusy(true); setErr('');
    try { const cv = await api.post<{ id: string }>(`/api/claims/${claimId}/contract/prepare`, { feePercent: fee.trim() === '' ? null : Number(fee) }); await api.post(`/api/contracts/${cv.id}/send`, {}); onCreated(); onClose(); nav(`/claims/${claimId}`); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function uploadSigned(file: File) {
    if (!claimId) return;
    setBusy(true); setErr('');
    try { const key = await uploadFile(file, 'misc'); await api.post(`/api/claims/${claimId}/contract/upload-signed`, { fileKey: key }); onCreated(); onClose(); nav(`/claims/${claimId}`); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 4000, overflow: 'auto' };
  const modal: React.CSSProperties = { background: 'var(--card)', borderRadius: 14, border: '1px solid var(--line)', width: 560, maxWidth: '100%', padding: 20 };
  const lbl: React.CSSProperties = { fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-semibold)' };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()} ref={dialogo.ref} {...dialogo.props}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}><Plus size={16} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('al.title')}</h3>
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={onClose}><X size={14} strokeWidth={1.75} /></button>
        </div>

        {!claimId ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={lbl}>{t('claims.homeowner')} *<input className="input sm" value={f.full_name} onChange={(e) => set('full_name', e.target.value)} /></label>
              <label style={lbl}>{t('cci.claimType')}<select className="input sm" value={f.claim_type} onChange={(e) => set('claim_type', e.target.value)}><option value="">—</option>{CLAIM_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}</select></label>
              <label style={lbl}>Phone<input className="input sm" value={f.phone} onChange={(e) => set('phone', e.target.value)} /></label>
              <label style={lbl}>Email<input className="input sm" value={f.email} onChange={(e) => set('email', e.target.value)} /></label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={lbl}>{t('cci.state')}
                <select className="input sm" value={f.state} onChange={(e) => set('state', e.target.value)}>
                  <option value="">—</option>{states.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label style={lbl}>{t('cci.language')}
                <select className="input sm" value={f.preferred_language} onChange={(e) => set('preferred_language', e.target.value)}>
                  <option value="">—</option><option value="english">English</option><option value="spanish">Spanish</option>
                </select>
              </label>
            </div>
            <AddressAutocomplete label={`${t('cci.address')} *`} value={f.address} onChange={(v) => setF((p) => ({ ...p, address: v, state: p.state || (stateFromAddress(v) ?? '') }))} />
            <label style={lbl}>{t('cci.gateCode')}<input className="input sm" value={f.gate_code} onChange={(e) => set('gate_code', e.target.value)} placeholder={t('cci.gateCodeHint')} /></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={lbl}>{t('al.contractor')}
                <select className="input sm" value={f.contractor_id} onChange={(e) => pickContractor(e.target.value)}>
                  <option value="">—</option>{contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label style={lbl}>{t('ct.accountManager')}
                <select className="input sm" value={f.account_team_id} onChange={(e) => set('account_team_id', e.target.value)}>
                  <option value="">—</option>{teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                </select>
              </label>
            </div>
            {contractor && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>PA: <b>{contractor.pa_name || '—'}</b>{contractor.fee_percent != null ? ` · Fee ${contractor.fee_percent}%` : ''}</div>}
            <label style={lbl}>{t('q.insurance')}
              {!insOther ? (
                <select className="input sm" value={f.insurance_company} onChange={(e) => { if (e.target.value === '__other') { setInsOther(true); set('insurance_company', ''); } else set('insurance_company', e.target.value); }}>
                  <option value="">—</option>
                  {insurers.map((i) => <option key={i.id} value={i.name}>{i.name}</option>)}
                  <option value="__other">{t('al.insOther')}</option>
                </select>
              ) : (
                <div style={{ display: 'grid', gap: 4 }}>
                  <input className="input sm" autoFocus value={f.insurance_company} onChange={(e) => set('insurance_company', e.target.value)} placeholder={t('al.insOtherPh')} />
                  <button type="button" className="btn ghost sm" style={{ justifySelf: 'start', padding: '0 4px' }} onClick={() => { setInsOther(false); set('insurance_company', ''); }}>← {t('al.insBackToList')}</button>
                </div>
              )}
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={lbl}>Policy #<input className="input sm" style={{ fontFamily: 'var(--font-mono)' }} value={f.policy_number} onChange={(e) => set('policy_number', e.target.value)} /></label>
              <label style={lbl}>Claim #<input className="input sm" style={{ fontFamily: 'var(--font-mono)' }} value={f.claim_number} onChange={(e) => set('claim_number', e.target.value)} /></label>
            </div>
            <label style={lbl}>{t('al.photosLink')}<input className="input sm" value={f.photos_link} onChange={(e) => set('photos_link', e.target.value)} placeholder="CompanyCam / Drive link" /></label>
            {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-sm)' }}>{err}</div>}
            <button className="btn" disabled={busy} onClick={create}>{busy ? '…' : t('al.create')}</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ color: 'var(--green)', fontWeight: 'var(--fw-bold)' }}><Check size={13} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} /> {t('al.created')}</div>
            <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('al.nextStep')}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ ...lbl, display: 'flex', gap: 6, alignItems: 'center' }}>{t('ird.fee')}:
                <input className="input sm" style={{ width: 74 }} type="number" step="0.1" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="%" /> %
              </label>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{feeSource === 'contractor' ? t('ird.feeFromContractor') : feeSource === 'state_default' ? t('ird.feeFromState') : t('ird.feeNone')}</span>
            </div>
            {/* PA que saldrá en el contrato: quién, con qué licencia y DE DÓNDE sale (el equipo
                asignado manda; la ficha del contratista es respaldo). El que firma es este mismo. */}
            {pa.blocked ? (
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--red)', fontWeight: 'var(--fw-semibold)' }}>
                {t('ct.paBlockedWarn', { pa: pa.blocked.pa_name, team: pa.blocked.team_name, state: pa.state ?? '—' })}
              </div>
            ) : pa.missing ? (
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--red)', fontWeight: 'var(--fw-semibold)' }}>{t('ct.paMissingWarn', { state: pa.state ?? '—' })}</div>
            ) : pa.name ? (
              <div style={{ fontSize: 'var(--fs-xs)' }}>
                <span className="muted">{t('ct.paLabel')}: </span><strong>{pa.name}</strong>
                {pa.license ? <span className="muted"> · Lic. {pa.license}{pa.state ? ` (${pa.state})` : ''}</span> : null}
                <span className="muted"> · {pa.from === 'team' ? t('ct.paFromTeam', { team: pa.team ?? '—' }) : t('ct.paFromBackup')}</span>
              </div>
            ) : null}
            {/* El estado del form contra el de la DIRECCIÓN. La plantilla se elige por el estado, así
                que un dropdown mal contestado manda el contrato de otro estado — y eso se descubría
                leyendo el PDF que el cliente ya había firmado (claims 2394 y 2437, contrato de PA
                sobre casas de GA). El backend ya no lo genera; acá se dice por qué, antes de apretar. */}
            {choqueEstado && (
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--red)', fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'] }}>
                {t('ct.stateMismatchWarn', { form: choqueEstado.form, address: choqueEstado.address })}
              </div>
            )}
            {/* QUÉ contrato va a salir. Solo se muestra cuando NO es la plantilla genérica del
                estado: un contratista con contrato especial (cláusula propia) tiene que verse
                ANTES de mandarlo, no descubrirse leyendo el PDF que el cliente ya firmó. */}
            {tpl?.special_for && (
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--orange)', fontWeight: 'var(--fw-semibold)' }}>
                <HardHat size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('ct.specialFor', { name: tpl.special_for })}
              </div>
            )}
            <button className="btn" disabled={busy || pa.missing || !!pa.blocked || !!choqueEstado} onClick={sendContract}><Mail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('al.sendContract')}</button>
            <FileDrop onFile={uploadSigned} accept="application/pdf" label={t('cx.uploadSigned')} busy={busy} busyLabel={t('cd.uploading')} compact />
            {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-sm)' }}>{err}</div>}
            <button className="btn ghost sm" onClick={() => { onClose(); nav(`/claims/${claimId}`); }}>{t('al.openClaim')} →</button>
          </div>
        )}
      </div>
    </div>
  );
}
